import { useEffect, useRef, useState } from "react";
import { api } from "../api.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";
import type { AnalisisMotivo } from "../../../dominio/src/index.ts";

type Analisis = AnalisisMotivo & { motor: "ia" | "reglas" };

/**
 * Donna interpreta el motivo de consulta y sugiere la enfermedad actual.
 * Nada se guarda solo: el profesional decide si usa la sugerencia, la edita o la descarta.
 */
export function SugerenciaMotivo({ pacienteId, motivo, enfermedad, alUsar, automatico = false }: {
  pacienteId: string; motivo: string; enfermedad: string; alUsar: (texto: string, modo: "reemplazar" | "agregar") => void; automatico?: boolean;
}) {
  const [an, setAn] = useState<Analisis | null>(null);
  const [texto, setTexto] = useState("");
  const [usada, setUsada] = useState(false);
  const a = useAccion();
  const ultima = useRef("");

  async function sugerir() {
    setUsada(false);
    ultima.current = `${motivo}|${enfermedad}`;
    const r = await a.correr(() => api<{ analisis: Analisis | null }>("POST", "/api/dictado/motivo", { motivo, enfermedad_actual: enfermedad, paciente_id: pacienteId }));
    if (r) { setAn(r.analisis); setTexto(r.analisis?.sugerencia ?? ""); }
  }
  useEffect(() => { if (automatico && (motivo.trim() || enfermedad.trim()) && ultima.current !== `${motivo}|${enfermedad}`) sugerir(); }, [automatico]);

  const vacio = !motivo.trim() && !enfermedad.trim();
  const cambio = an && ultima.current !== `${motivo}|${enfermedad}`;
  return (
    <section className="sugerencia" aria-label="Sugerencia de Donna">
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <b>Donna: motivo y enfermedad actual</b>
        <button type="button" className="btn chico" onClick={sugerir} disabled={a.ocupado || vacio}>
          {a.ocupado ? "Analizando…" : an ? (cambio ? "Actualizar sugerencia" : "Volver a sugerir") : "Sugerir enfermedad actual"}
        </button>
      </div>
      {vacio && !an && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Escribe o dicta el motivo de consulta y Donna te sugiere la enfermedad actual y qué preguntar.</p>}
      <AvisoError error={a.error} />
      {an && (
        <>
          <div className="fila" style={{ gap: 6, flexWrap: "wrap" }}>
            <span className="chip rojo">{an.nombre}</span>
            {an.otras.map((o) => <span key={o} className="chip">{o}</span>)}
            <span className="muted" style={{ fontSize: 12 }}>{an.motor === "ia" ? "Redacción con IA, solo con lo dicho" : "Redacción de Donna, solo con lo dicho"}</span>
          </div>
          {an.alertas.map((x) => <div key={x} className="aviso error" role="alert">⚠ {x}</div>)}
          {an.datos.length > 0 && (
            <dl className="datos-motivo">
              {an.datos.map((d) => <div key={d.clave}><dt>{d.etiqueta}</dt><dd>{d.valor}</dd></div>)}
            </dl>
          )}
          <label className="campo">Enfermedad actual sugerida
            <textarea rows={3} value={texto} onChange={(e) => { setTexto(e.target.value); setUsada(false); }} aria-label="Enfermedad actual sugerida" />
          </label>
          <div className="fila">
            <button type="button" className="btn pri chico" disabled={!texto.trim()} onClick={() => { alUsar(texto.trim(), "reemplazar"); setUsada(true); }}>Usar en enfermedad actual</button>
            {enfermedad.trim() && <button type="button" className="btn chico" disabled={!texto.trim()} onClick={() => { alUsar(texto.trim(), "agregar"); setUsada(true); }}>Agregar a lo escrito</button>}
            {usada && <span className="muted" style={{ fontSize: 13 }}>Listo: revisa y guarda.</span>}
          </div>
          {an.preguntas.length > 0 && (
            <div>
              <h4 style={{ margin: "6px 0 4px" }}>Falta preguntar</h4>
              <ul className="preguntas">{an.preguntas.map((p) => <li key={p}>{p}</li>)}</ul>
              <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>Dicta o escribe las respuestas en la enfermedad actual y pulsa «Actualizar sugerencia».</p>
            </div>
          )}
          {an.orientacion.length > 0 && (
            <div>
              <h4 style={{ margin: "6px 0 4px" }}>Orientación diagnóstica <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>(a confirmar con el examen clínico y radiográfico)</span></h4>
              <ul className="preguntas">
                {an.orientacion.map((o) => <li key={o.nombre}><b>{o.nombre}</b>{o.cie10 && <span className="mono"> · {o.cie10}</span>} <span className="muted">— {o.porque}</span></li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
